import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import type { User } from "@/types";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { getLevel } from "@/types";

// Shape ritornata da GET /api/student/dashboard
interface AssignedRow {
  assignmentId: string;
  testId: string;
  titolo: string;
  slug: string;
  descrizione: string | null;
  durataMinuti: number | null;
  pubblicoAnonimo: boolean;
  status: string;
  assignedAt: string;
  accessToken: string | null;
  ultimoCanale: string | null;
  submissionId: string | null;        // se != null → c'è una submission in corso (resume)
  submissionStartedAt: string | null;
}

interface HistoryRow {
  id: string;
  testId: string;
  titolo: string;
  slug: string;
  startedAt: string;
  completedAt: string;
  scoreTotale: number | null;
  scoreMassimo: number | null;
  percentuale: string | number | null;
  livello: string | null;
}

interface DashboardData {
  user: User;
  assigned: AssignedRow[];
  history: HistoryRow[];
}

export default function StudentDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<DashboardData>("/student/dashboard")
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} /></div>;
  }

  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="text-center">
          <p className="text-red-300">{err ?? "Errore caricamento"}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">
      <header className="flex justify-between items-baseline">
        <div>
          <div className="font-mono text-xs uppercase tracking-wider text-secondary">
            Area personale
          </div>
          <h1 className="text-3xl font-serif text-primary">
            Ciao {data.user.nome ?? data.user.email}
          </h1>
        </div>
        <button
          onClick={() => { localStorage.removeItem("jwt"); window.location.href = "/accedi"; }}
          className="font-mono text-xs text-secondary hover:text-primary">
          esci →
        </button>
      </header>

      <section className="space-y-3">
        <h2 className="font-mono text-xs uppercase tracking-wider text-secondary">
          Test assegnati ({data.assigned.length})
        </h2>
        {data.assigned.length === 0 ? (
          <Card>
            <p className="text-secondary text-sm">
              Nessun test assegnato al momento. Tornerai qui quando il docente
              te ne assegnerà uno.
            </p>
          </Card>
        ) : (
          data.assigned.map((t) => (
            <Card key={t.assignmentId}>
              <div className="flex justify-between items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-primary text-lg font-serif">{t.titolo}</div>
                  {t.descrizione && (
                    <div className="text-secondary text-sm mt-1">{t.descrizione}</div>
                  )}
                  <div className="text-xs text-muted font-mono mt-1 flex gap-2">
                    {t.durataMinuti && <span>{t.durataMinuti} min</span>}
                    {t.submissionId && <span className="text-accent">· ripreso</span>}
                  </div>
                </div>
                {t.submissionId ? (
                  <Link to={`/area/test/${t.submissionId}`}
                    className="bg-accent text-white px-4 py-2 rounded-xl font-mono text-sm hover:brightness-110">
                    Riprendi →
                  </Link>
                ) : (
                  <Link to={`/test/${t.slug}`}
                    className="bg-accent text-white px-4 py-2 rounded-xl font-mono text-sm hover:brightness-110">
                    Inizia →
                  </Link>
                )}
              </div>
            </Card>
          ))
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-mono text-xs uppercase tracking-wider text-secondary">
          Storico risultati ({data.history.length})
        </h2>
        {data.history.length === 0 ? (
          <Card>
            <p className="text-secondary text-sm">Nessun test completato.</p>
          </Card>
        ) : (
          data.history.map((s) => {
            const lv = getLevel(s.scoreTotale ?? 0, s.scoreMassimo ?? 0);
            const pct = s.percentuale != null ? Math.round(Number(s.percentuale)) : null;
            return (
              <Link key={s.id} to={`/area/risultati/${s.id}`} className="block">
                <Card className="hover:border-secondary transition-colors">
                  <div className="flex justify-between items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-primary truncate">{s.titolo}</div>
                      <div className="text-xs text-muted font-mono mt-1">
                        {new Date(s.completedAt).toLocaleDateString("it-IT")}
                        {pct != null && ` · ${pct}%`}
                      </div>
                    </div>
                    <Badge color={lv.color}>{lv.label}</Badge>
                  </div>
                </Card>
              </Link>
            );
          })
        )}
      </section>
    </div>
  );
}
